import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, CheckCircle, XCircle, Clock, Filter, Search, Eye, Plus, Calculator, Ban, ChevronRight, MoreHorizontal, Info, CheckCircle2} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { fetchAllLoanHistory, getEmployees, changeLoanRequestStatus, applyLoan, sendOtptoAdmin, verifyLoanOTP } from "@/services/adminService";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis, PaginationPageSize } from "@/components/ui/pagination";
import LoanApplyDialog from "@/components/LoanApplyDialog";
import LoanPrepaymentWizard from "@/components/admin/loans/LoanPrepaymentWizard";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import OtpModal from "@/components/OtpModal";
import { useAuth } from "@/contexts/AuthContext";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const LoanManagement = () => {
  const { user } = useAuth();
  const [loanRequests, setLoanRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [disbursedAmount, setDisbursedAmount] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showLoanDetailsDialog, setShowLoanDetailsDialog] = useState(false);
  const [showNewLoanDialog, setShowNewLoanDialog] = useState(false);
  const [loanTypes, setLoanTypes] = useState([]);
  const [selectedSalary, setSelectedSalary] = useState("");
  const [selectedExistingEMI, setSelectedExistingEMI] = useState(0);
  const [applyLoanConfig, setApplyLoanConfig] = useState({
    affordable: true,
    maxEligible: 0,
    maxEMI: 0,
  });
  const [newLoan, setNewLoan] = useState({
    employee_id: "",
    type: "",
    amount: "",
    interest_rate: 0,
    tenure_months: "",
    purpose: "",
    guarantor_employee_id: "",
  });
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [prepaymentOpen, setPrepaymentOpen] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1); // reset to first page when page size changes
  };
  const { can } = usePermission();
  const { canView, canCreate, canEdit } = createCanHelpers(can);

  const canViewLoans = canView("loan_management");
  const canCreateLoan = canCreate("loan_management");
  const canEditLoan = canEdit("loan_management");
  const totalItems = filteredRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedRequests = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRequests.slice(start, start + pageSize);
  }, [filteredRequests, page, pageSize]);

  const buildPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  useEffect(() => {
    fetchLoanRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [loanRequests, searchTerm, statusFilter]);

  const fetchLoanRequests = async () => {
    try {
      //  employeesResponse

      const loansResponse = await fetchAllLoanHistory();
      const employeesResponse = await getEmployees();

      const loans = loansResponse.data.loanHistory;
      setLoanTypes(loansResponse.data.loanTypes);
      const employeesData = employeesResponse;

      // Store employees for use in new loan form
      setEmployees(employeesData);

      // Create employee map for quick lookup
      const employeeMap = {};
      employeesData.forEach((emp) => {
        employeeMap[emp.employee_id] = emp.name;
      });

      // Add employee names to loan requests
      const loansWithNames = loans.map((loan) => ({
        ...loan,
        employeeName: loan.employeeName,
      }));

      setLoanRequests(loansWithNames);
    } catch (error) {
      console.error("Error fetching loan requests:", error);
      toast.error("Failed to load loan requests");
    } finally {
      setLoading(false);
    }
  };

  const calculateEMI = (principal, rate, tenure) => {
    if (!principal || !tenure) return 0;

    // For 0% interest, EMI is simply principal divided by tenure
    if (rate === 0 || rate === "0") {
      return Math.round(principal / tenure);
    }

    // For interest-bearing loans, use compound interest formula
    const monthlyRate = rate / 100 / 12;
    const emi =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
      (Math.pow(1 + monthlyRate, tenure) - 1);
    return Math.round(emi);
  };

  const checkAdminLoanEligibility = (
    salary,
    existingEMI,
    amount,
    rate,
    tenure
  ) => {
    const gross = Number(salary) || 0;
    const existing = Number(existingEMI) || 0;
    const principal = Number(amount) || 0;
    const interest = Number(rate) || 0;
    const N = Number(tenure) || 0;

    // Convert to monthly rate
    const R = interest / 100 / 12;

    // Calculate EMI
    let estimatedEMI = 0;
    if (R === 0) {
      estimatedEMI = principal / N;
    } else {
      estimatedEMI =
        (principal * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
    }
    estimatedEMI = Math.round(estimatedEMI);

    // DTI rule: employee can use 50% of salary
    const maxEMICapacity = gross * 0.5 - existing;

    // Calculate maximum eligible loan amount
    let maxLoanAmount = 0;
    if (maxEMICapacity > 0) {
      if (R === 0) {
        maxLoanAmount = maxEMICapacity * N;
      } else {
        const factor = Math.pow(1 + R, N);
        maxLoanAmount = maxEMICapacity * ((factor - 1) / (R * factor));
      }
    }
    // Round down to nearest 1000
    maxLoanAmount = Math.floor(maxLoanAmount / 1000) * 1000;

    return {
      estimatedEMI,
      maxEMI: Math.max(0, maxEMICapacity),
      maxEligible: Math.max(0, maxLoanAmount),
      affordable: estimatedEMI <= maxEMICapacity,
    };
  };

  const handleCreateLoan = async () => {
    try {
      const emi = calculateEMI(
        parseFloat(newLoan.amount),
        parseFloat(newLoan.interest_rate),
        parseInt(newLoan.tenure_months)
      );

      const { affordable, maxEligible, maxEMI } = checkAdminLoanEligibility(
        selectedSalary,
        selectedExistingEMI,
        newLoan.amount,
        newLoan.interest_rate,
        newLoan.tenure_months
      );

      setApplyLoanConfig((prev) => ({
        ...prev,
        affordable: affordable,
        maxEligible: maxEligible,
        maxEMI: maxEMI,
      }));
      console.log(
        `Max loan: ${maxEligible}, max EMI: ${maxEMI} avaliable monthly salary: ${selectedSalary - selectedExistingEMI
        }`
      );

      if (!affordable) return;

      const loanData = {
        employee_id: newLoan.employee_id,
        loan_type: newLoan.type,
        amount: parseFloat(newLoan.amount),
        tenure_months: parseInt(newLoan.tenure_months),
        interest_rate: parseFloat(newLoan.interest_rate),
        monthly_emi: emi,
        purpose: newLoan.purpose,
      };

      await applyLoan(loanData);

      setApplyLoanConfig({
        affordable: true,
        maxEligible: 0,
        maxEMI: 0,
      });

      setSelectedSalary("");
      setNewLoan({
        employee_id: "",
        type: "",
        amount: "",
        interest_rate: 0,
        tenure_months: "",
        purpose: "",
        guarantor_employee_id: "",
      });
      setShowNewLoanDialog(false);
      toast.success("Loan application created successfully");
      fetchLoanRequests();
    } catch (error) {
      console.error("Error creating loan:", error);
      toast.error("Failed to create loan application");
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString() || 0}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return format(new Date(date), "dd MMM yyyy");
  };

  const filterRequests = () => {
    let filtered = loanRequests;

    if (searchTerm) {
      filtered = filtered.filter(
        (request) =>
          request.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (request.employeeName &&
            request.employeeName
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (request.purpose || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          request.loan_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((request) => {
        if (statusFilter === "completed") {
          return (
            request.status === "completed" || request.status === "disbursed"
          );
        }

        return request.status === statusFilter;
      });
    }

    setFilteredRequests(filtered);
  };

  const handleApproveReject = async (
    loanId,
    status,
    reason = "",
    disbursed = null
  ) => {
    setActionLoading(true);
    try {
      const data = { status };
      if (status === "rejected" && reason) {
        data.rejection_reason = reason;
      }
      if (status === "approved" && disbursed) {
        data.disbursed_amount = parseFloat(disbursed);
      }

      const res = await changeLoanRequestStatus(loanId, data);

      if (res) fetchLoanRequests();

      // Refresh sidebar notification badges
      if (window.refreshSidebarNotifications) {
        window.refreshSidebarNotifications();
      }

      // Reset state
      setSelectedRequest(null);
      setDisbursedAmount("");
      setRejectionReason("");

      toast.success(`Loan request ${status} successfully`);
    } catch (error) {
      console.error("Error updating loan request:", error);
      toast.error(
        error.response?.data?.detail || "Failed to update loan request"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const displayStatus = getDisplayStatus(status);

    const base = "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide";

    switch (displayStatus) {
      case "pending":
        return (
          <span className={`${base} border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200`}>
            Pending
          </span>
        );

      case "approved":
        return (
          <span className={`${base} border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200`}>
            Approved
          </span>
        );

      case "completed":
        return (
          <span className={`${base} border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200`}>
            Completed
          </span>
        );

      case "rejected":
        return (
          <span className={`${base} border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200`}>
            Rejected
          </span>
        );

      case "cancelled":
        return (
          <span className={`${base} border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200`}>
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const getStats = () => {
    const total = loanRequests.length;
    const pending = loanRequests.filter((r) => r.status === "pending").length;
    const approved = loanRequests.filter((r) => r.status === "approved" || r.status === "disbursed").length;
    const rejected = loanRequests.filter((r) => r.status === "rejected" || r.status === "cancelled").length;
    const totalAmount = loanRequests
      .filter((r) => r.status === "approved")
      .reduce((sum, r) => sum + (r.disbursed_amount || r.amount), 0);
    const totalOutstanding = loanRequests
      .filter((r) => r.status === "approved")
      .reduce((sum, r) => sum + (r.outstanding_amount || r.amount || 0), 0);

    const totalDisbursed = loanRequests
      .filter((r) => r.status === "approved" || r.status === "disbursed")
      .reduce((sum, r) => {
        return sum + Number(r.disbursed_amount ?? 0);
      }, 0);

    return {
      total,
      pending,
      approved,
      rejected,
      totalAmount,
      totalOutstanding,
      totalDisbursed,
    };
  };

  const getDisplayStatus = (status) => {
    if (status === "disbursed") return "completed";
    return status;
  };

  const stats = getStats();

  const handleCancelLoan = async () => {
    if (!selectedRequest?._id) return;

    try {
      await changeLoanRequestStatus(selectedRequest._id, {
        status: "cancelled",
      });

      toast.success("Loan cancelled successfully");

      // Close dialogs & refresh data
      setShowLoanDetailsDialog(false);
      setSelectedRequest(null);
      fetchLoanRequests();
      setCancelDialogOpen(false);
    } catch (error) {
      console.error("Cancel loan failed:", error);
      toast.error("Failed to cancel loan");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-52" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-44" />
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Loan Management
          </h1>
          <p className="text-gray-500">
            Review and approve employee loan requests
          </p>
        </div>
        <Button
          disabled={!canCreateLoan}
          onClick={() => {
            if (!canCreate("loan_management", true)) return;
            setShowNewLoanDialog(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Loan Application
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Applications
                </p>
                <p className="text-2xl font-bold text-primary">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Pending
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-500">
                  {stats.pending}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Approved
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-500">
                  {stats.approved}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Rejected / Cancelled
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-500">
                  {stats.rejected}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Disbursed
                </p>
                <p className="text-2xl font-bold text-primary">
                  ₹{stats.totalDisbursed.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Outstanding
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-500">
                  ₹{stats.totalOutstanding.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by employee name, ID, purpose, or loan type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loan Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Loan Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Employee
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Loan Type
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Amount
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Tenure
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    EMI
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Applied On
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Status
                  </TableHead>
                  <TableHead className=" font-semibold text-center w-[200px]">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedRequests.map((request) => (
                  <TableRow
                    key={request.id}
                    className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowLoanDetailsDialog(true);
                    }}
                  >
                    <TableCell className="font-medium">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-200">
                          {request.employeeName}
                        </p>
                        <p className="text-sm text-muted-foreground dark:text-gray-400">
                          {request.employeeId}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      {request.loan_type}
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      ₹{request.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      {request.tenure_months} months
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      ₹{request.monthly_emi.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-gray-800 dark:text-gray-300">
                      {new Date(request.createdAt).toDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(request.status)}
                      </div>
                    </TableCell>
                    <TableCell
                      className="pr-6 py-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center group/btn"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowLoanDetailsDialog(true);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5 mr-2 opacity-60 group-hover/btn:text-[#3194a0] group-hover/btn:opacity-100 transition-all" />
                          <span className="group-hover/btn:text-[#3194a0] transition-colors">
                            View
                          </span>
                          <ChevronRight className="w-3 h-3 ml-1 opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:text-[#3194a0] transition-all" />
                        </Button>

                        {/* Divider */}
                        <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-0.5" />

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all rounded-md"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-52 p-1.5 shadow-xl border-slate-200 dark:border-slate-800"
                          >
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-2 py-1.5">
                              Request Actions
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />

                            {request.status === "pending" ? (
                              <>
                                <DropdownMenuItem
                                  className="text-emerald-600 cursor-pointer text-xs font-bold focus:text-emerald-700 focus:bg-emerald-50/50 rounded-md py-2.5 px-2.5 mb-0.5 group"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setDisbursedAmount(
                                      request.amount.toString(),
                                    );
                                    setApproveDialogOpen(true);
                                  }}
                                >
                                  <div className="w-7 h-7 rounded-full bg-emerald-50 group-focus:bg-emerald-100 flex items-center justify-center mr-3 transition-colors">
                                    <CheckCircle2 className="w-4 h-4" />
                                  </div>
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-rose-600 cursor-pointer text-xs font-bold focus:text-rose-700 focus:bg-rose-50/50 rounded-md py-2.5 px-2.5 group"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setRejectDialogOpen(true);
                                  }}
                                >
                                  <div className="w-7 h-7 rounded-full bg-rose-50 group-focus:bg-rose-100 flex items-center justify-center mr-3 transition-colors">
                                    <XCircle className="w-4 h-4" />
                                  </div>
                                  Reject
                                </DropdownMenuItem>
                              </>
                            ) : request.status === "approved" ? (
                              <DropdownMenuItem
                                className="text-rose-600 cursor-pointer text-xs font-bold focus:text-rose-700 focus:bg-rose-50/50 rounded-md py-2.5 px-2.5 group"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setCancelDialogOpen(true);
                                }}
                              >
                                <div className="w-7 h-7 rounded-full bg-rose-50 group-focus:bg-rose-100 flex items-center justify-center mr-3 transition-colors">
                                  <Ban className="w-4 h-4" />
                                </div>
                                Cancel
                              </DropdownMenuItem>
                            ) : request.status === "completed" ? (
                              <DropdownMenuItem
                                className="text-indigo-600 cursor-pointer text-xs font-bold focus:text-indigo-700 focus:bg-indigo-50/50 rounded-md py-2.5 px-2.5 group"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setPrepaymentOpen(true);
                                }}
                              >
                                <div className="w-7 h-7 rounded-full bg-indigo-50 group-focus:bg-indigo-100 flex items-center justify-center mr-3 transition-colors">
                                  <Calculator className="w-4 h-4" />
                                </div>
                                Prepay
                              </DropdownMenuItem>
                            ) : (
                              <div className="px-2 py-3 flex items-center justify-center bg-slate-50/50 dark:bg-slate-800/30 rounded-md border border-dashed border-slate-200 dark:border-slate-700 my-1">
                                <span className="text-[11px] text-slate-400 font-medium italic flex items-center gap-1.5">
                                  <Info className="w-3 h-3" /> No pending
                                  actions
                                </span>
                              </div>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredRequests.length === 0 && (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No loan requests found
              </h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "Loan requests from employees will appear here"}
              </p>
            </div>
          )}

          {filteredRequests.length > 0 && (
            <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
              <PaginationPageSize
                value={pageSize}
                onValueChange={handlePageSizeChange}
              />
              <Pagination className="mx-0 justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      aria-disabled={page === 1}
                      className={
                        page === 1 ? "pointer-events-none opacity-50" : ""
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.max(1, p - 1));
                      }}
                    />
                  </PaginationItem>

                  {buildPages(page, totalPages).map((p, idx) => {
                    if (p === "...") {
                      return (
                        <PaginationItem key={`e-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }

                    return (
                      <PaginationItem key={p}>
                        <PaginationLink
                          href="#"
                          isActive={p === page}
                          onClick={(e) => {
                            e.preventDefault();
                            setPage(p);
                          }}
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      aria-disabled={page === totalPages}
                      className={
                        page === totalPages
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.min(totalPages, p + 1));
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Loan Application Dialog */}
      <LoanApplyDialog
        open={showNewLoanDialog}
        onOpenChange={setShowNewLoanDialog}
        mode="admin"
        title="New Loan Application"
        maxWidthClass="max-w-3xl"
        submitLabel="Submit Application"
        loanTypes={loanTypes}
        employees={employees}
        onSubmit={async (loanData) => {
          try {
            await applyLoan(loanData);
            toast.success("Loan application created successfully");
            fetchLoanRequests();
          } catch (error) {
            if (!isAuthExpiryError(error)) {
              toast.error(
                getErrorMessage(error, "Failed to create loan application"),
              );
            }
          }
        }}
      />

      {/* Loan Details Dialog */}
      <Dialog
        open={showLoanDetailsDialog}
        onOpenChange={setShowLoanDetailsDialog}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-gray-900 border border-gray-200 dark:bg-[#0f172a] dark:text-gray-100 dark:border-[#1e293b]">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-gray-100">
              Loan Details - {selectedRequest?.employeeName}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* ---- TOP GRID ---- */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Loan Information */}
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 backdrop-blur-sm dark:bg-[#1e293b]/40 dark:border-[#334155]">
                  <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
                    Loan Information
                  </h3>

                  <div className="space-y-2 text-sm">
                    {[
                      ["Loan Type:", selectedRequest.loan_type],
                      ["Amount:", formatCurrency(selectedRequest.amount)],
                      [
                        "Total Payable:",
                        formatCurrency(selectedRequest.total_payable),
                      ],
                      ["Interest Rate:", `${selectedRequest.interest_rate}%`],
                      ["Tenure:", `${selectedRequest.tenure_months} months`],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex justify-between px-3 py-2 rounded-lg bg-white border border-gray-200 dark:bg-[#0f172a]/40 dark:border-[#1e293b]"
                      >
                        <span className="text-gray-600 dark:text-gray-400">
                          {label}
                        </span>
                        <span className="text-gray-900 font-medium dark:text-gray-100">
                          {value}
                        </span>
                      </div>
                    ))}

                    {/* EMI Highlight */}
                    <div className="flex justify-between px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
                      <span className="text-orange-700 dark:text-orange-400">
                        Monthly EMI:
                      </span>
                      <span className="font-semibold text-orange-700 dark:text-orange-300">
                        {formatCurrency(selectedRequest.monthly_emi)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Repayment Status */}
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 backdrop-blur-sm dark:bg-[#1e293b]/40 dark:border-[#334155]">
                  <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
                    Repayment Status
                  </h3>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        Disbursed Amount:
                      </span>
                      <span className="text-blue-700 font-semibold dark:text-blue-300">
                        {formatCurrency(selectedRequest.disbursed_amount || 0)}
                      </span>
                    </div>

                    <div className="flex justify-between px-3 py-2 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        Outstanding:
                      </span>
                      <span className="text-red-700 font-semibold dark:text-red-400">
                        {formatCurrency(
                          selectedRequest.outstanding_amount ??
                            selectedRequest.total_payable ??
                            0,
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between px-3 py-2 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        Paid EMIs:
                      </span>
                      <span className="text-green-700 font-medium dark:text-green-300">
                        {selectedRequest.paid_emis || 0} /{" "}
                        {selectedRequest.tenure_months}
                      </span>
                    </div>

                    <div className="flex justify-between px-3 py-2 rounded-lg bg-white border border-gray-200 dark:bg-[#0f172a]/40 dark:border-[#1e293b]">
                      <span className="text-gray-600 dark:text-gray-400">
                        Progress:
                      </span>
                      <span className="text-gray-900 font-medium dark:text-gray-100">
                        {(
                          (Number(selectedRequest.disbursed_amount || 0) /
                            Number(selectedRequest.total_payable || 1)) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-2 p-3 rounded-lg bg-white border border-gray-200 dark:bg-[#0f172a]/40 dark:border-[#1e293b]">
                    <Progress
                      value={
                        (Number(selectedRequest.disbursed_amount || 0) /
                          Number(selectedRequest.total_payable || 1)) *
                        100
                      }
                      className="h-3 bg-gray-200 dark:bg-[#1e293b]"
                      indicatorClassName="bg-green-500"
                    />
                    <p className="text-xs text-gray-500 mt-2">Progress</p>
                  </div>
                </div>
              </div>

              {/* ---- Additional Details ---- */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 backdrop-blur-sm dark:bg-[#1e293b]/40 dark:border-[#334155]">
                <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  Additional Details
                </h3>

                <div className="grid gap-4 md:grid-cols-2 text-sm">
                  <div className="rounded-lg bg-white border border-gray-200 p-3 dark:bg-[#0f172a]/40 dark:border-[#1e293b]">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Purpose:
                    </span>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">
                      {selectedRequest.purpose ?? "-"}
                    </p>
                  </div>

                  <div className="rounded-lg bg-white border border-gray-200 p-3 dark:bg-[#0f172a]/40 dark:border-[#1e293b]">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Status:
                    </span>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 capitalize">
                      {selectedRequest.status ?? "-"}
                    </p>
                  </div>

                  <div className="rounded-lg bg-white border border-gray-200 p-3 dark:bg-[#0f172a]/40 dark:border-[#1e293b]">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Applied Date:
                    </span>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      {formatDate(selectedRequest.createdAt)}
                    </p>
                  </div>

                  {["approved", "rejected"].includes(
                    selectedRequest.status,
                  ) && (
                    <div className="rounded-lg bg-white border border-gray-200 p-3 dark:bg-[#0f172a]/40 dark:border-[#1e293b]">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {selectedRequest.status === "approved"
                          ? "Approved Date:"
                          : selectedRequest.status === "rejected"
                            ? "Rejected Date:"
                            : ""}
                      </span>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {selectedRequest.approved_date
                          ? formatDate(selectedRequest.approved_date)
                          : selectedRequest.rejected_date
                            ? formatDate(selectedRequest.rejected_date)
                            : "Pending"}
                      </p>
                    </div>
                  )}

                  {selectedRequest.rejection_reason && (
                    <div className="md:col-span-2 rounded-lg bg-red-50 border border-red-200 p-3 dark:bg-red-900/20 dark:border-red-800">
                      <span className="font-medium text-red-700 dark:text-red-300">
                        Rejection Reason:
                      </span>
                      <p className="text-red-700 dark:text-red-400 mt-1 whitespace-pre-wrap">
                        {selectedRequest.rejection_reason ?? "-"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {selectedRequest?.status === "approved" && (
            <Button variant="outline" onClick={() => setPrepaymentOpen(true)}>
              Prepay Loan
            </Button>
          )}

          <LoanPrepaymentWizard
            open={prepaymentOpen}
            onClose={setPrepaymentOpen}
            loan={selectedRequest}
            onConfirm={async (data) => {
              toast.success("Prepayment successful");
              setPrepaymentOpen(false);
              fetchLoanRequests();
              setShowLoanDetailsDialog(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Loan Request</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Employee</p>
                  <p className="font-medium">{selectedRequest.employeeName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Loan Type</p>
                  <p className="font-medium">{selectedRequest.loan_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Requested Amount</p>
                  <p className="font-medium">₹{selectedRequest.amount}</p>
                </div>
              </div>

              <div>
                <Label>Disbursed Amount</Label>
                <Input
                  value={disbursedAmount}
                  onChange={(e) => setDisbursedAmount(e.target.value)}
                />
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setApproveDialogOpen(false);
                    setSelectedRequest(null);
                    setDisbursedAmount("");
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={async () => {
                    await handleApproveReject(
                      selectedRequest._id,
                      "approved",
                      "",
                      disbursedAmount,
                    );
                    setApproveDialogOpen(false);
                  }}
                >
                  Approve Loan
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Loan Request</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Reason for rejection"
                rows={4}
              />

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setRejectDialogOpen(false);
                    setSelectedRequest(null);
                    setRejectionReason("");
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="destructive"
                  disabled={!rejectionReason.trim() && !canEditLoan}
                  onClick={async () => {
                    if (!canEdit("loan_management", true)) return;
                    await handleApproveReject(
                      selectedRequest._id,
                      "rejected",
                      rejectionReason,
                    );
                    setRejectDialogOpen(false);
                  }}
                >
                  Reject Loan
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancel Approved Loan"
        message="Are you sure you want to cancel this approved loan? This will stop further EMIs and mark the loan as cancelled."
        confirmText="Confirm Cancel"
        cancelText="Keep Loan"
        onConfirm={async () => {
          if (!canEdit("loan_management", true)) return;
          try {
            setShowOtpModal(true);
            await sendOtptoAdmin();
          } catch (error) {
            setShowOtpModal(false);
            if (!isAuthExpiryError(error)) {
              toast.error(getErrorMessage(error, "Failed to send OTP"));
            }
          }
        }}
      />
      <OtpModal
        open={showOtpModal}
        onOpenChange={setShowOtpModal}
        otp={otp}
        setOtp={setOtp}
        resendOTP={async () => {
          try {
            await sendOtptoAdmin();
            toast.success("OTP resend successfully!");
          } catch (error) {
            toast.error("Failed to re send TOP");
          }
        }}
        onVerify={async () => {
          try {
            const res = await verifyLoanOTP(otp);
            if (!res?.success) toast.error("OTP verification failed");
            await handleCancelLoan();
            setShowOtpModal(false);
            setOtp("");
          } catch (err) {
            toast.error(err.response.data.error || "Invalid or expired OTP");
          }
        }}
        email={user.email}
      />
    </div>
  );
};

export default LoanManagement;
